import type { Workbook as ExcelWorkbook } from 'exceljs';
import {
  getCartera,
  getEstadosCuenta,
  type ReportStatus,
} from '@/lib/api';
import { exportBulkStatementsPDF } from '@/utils/pdf';

/** Thrown when there is nothing to export, so the toolbar can show a friendly toast. */
export class EmptyExportError extends Error {
  constructor() {
    super('empty-export');
    this.name = 'EmptyExportError';
  }
}

const MONEY_FMT = '"Q"#,##0.00';
const PCT_FMT = '0.00%';
const METHOD_LABEL: Record<string, string> = { french: 'Francés', german: 'Alemán' };
const SALE_LABEL: Record<string, string> = { financed: 'Financiado', cash: 'Contado' };
const STATUS_LABEL: Record<string, string> = { draft: 'Borrador', active: 'Activo', completed: 'Pagado', cancelled: 'Cancelado' };
const WATER_LABEL: Record<string, string> = { paid: 'Pagado', due: 'Por cobrar', pending: 'Pendiente', na: 'No aplica' };
const INST_STATUS: Record<string, string> = { pending: 'Pendiente', partial: 'Parcial', paid: 'Pagada', overdue: 'Vencida' };
const PAY_METHOD: Record<string, string> = { cash: 'Efectivo', transfer: 'Transferencia', deposit: 'Depósito', check: 'Cheque' };

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function slug(s: string): string {
  return s.replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'Proyecto';
}

function terreno(manzana: string | null, lotNumber: string | null): string {
  if (!manzana && !lotNumber) return '';
  return `${manzana ?? ''}${manzana && lotNumber ? '-' : ''}${lotNumber ?? ''}`;
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

async function newWorkbook(): Promise<ExcelWorkbook> {
  const { Workbook } = await import('exceljs');
  return new Workbook();
}

async function saveWorkbook(wb: ExcelWorkbook, filename: string) {
  const buf = await wb.xlsx.writeBuffer();
  downloadBlob(
    new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    filename,
  );
}

// exceljs Worksheet type is structural; use `ReturnType` off the workbook to avoid a second import.
function finishSheet(ws: ReturnType<ExcelWorkbook['addWorksheet']>) {
  ws.getRow(1).font = { bold: true };
  ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: ws.columnCount } };
  ws.views = [{ state: 'frozen', ySplit: 1 }];
}

export async function exportCartera(
  projectId: string,
  projectLabel: string,
  status: ReportStatus,
): Promise<void> {
  const { contracts, clients } = await getCartera(projectId, status);
  if (contracts.length === 0 && clients.length === 0) throw new EmptyExportError();

  const wb = await newWorkbook();

  const cartera = wb.addWorksheet('Cartera');
  cartera.columns = [
    { header: 'Contrato', key: 'contractNumber', width: 16 },
    { header: 'Cliente', key: 'buyerName', width: 28 },
    { header: 'Código', key: 'buyerCode', width: 12 },
    { header: 'Vendedor', key: 'sellerName', width: 22 },
    { header: 'Terreno', key: 'terreno', width: 14 },
    { header: 'Tipo', key: 'saleType', width: 12 },
    { header: 'Estado', key: 'status', width: 12 },
    { header: 'Financiado', key: 'financedAmount', width: 16, style: { numFmt: MONEY_FMT } },
    { header: 'Plazo (meses)', key: 'termMonths', width: 13 },
    { header: 'Tasa anual', key: 'interestRateAnnual', width: 12, style: { numFmt: PCT_FMT } },
    { header: 'Método', key: 'method', width: 12 },
    { header: 'Inicio', key: 'startDate', width: 12 },
    { header: 'Saldo lote', key: 'outstanding', width: 16, style: { numFmt: MONEY_FMT } },
    { header: 'Cuotas vencidas', key: 'overdueCount', width: 15 },
    { header: 'Mora', key: 'moraOutstanding', width: 14, style: { numFmt: MONEY_FMT } },
    { header: 'Costo adm. pend.', key: 'adminFeeOutstanding', width: 16, style: { numFmt: MONEY_FMT } },
    { header: 'Saldo total', key: 'totalOutstanding', width: 16, style: { numFmt: MONEY_FMT } },
    { header: 'Próx. vencimiento', key: 'nextDueDate', width: 16 },
    { header: 'Derecho de agua', key: 'waterRightStatus', width: 16 },
  ];
  contracts.forEach((r) => {
    cartera.addRow({
      contractNumber: r.contractNumber,
      buyerName: r.buyerName,
      buyerCode: r.buyerCode ?? '',
      sellerName: r.sellerName ?? '',
      terreno: terreno(r.manzana, r.lotNumber),
      saleType: SALE_LABEL[r.saleType] ?? r.saleType,
      status: STATUS_LABEL[r.status] ?? r.status,
      financedAmount: Number(r.financedAmount),
      termMonths: r.termMonths,
      interestRateAnnual: Number(r.interestRateAnnual),
      method: METHOD_LABEL[r.method] ?? r.method,
      startDate: r.startDate,
      outstanding: Number(r.outstanding),
      overdueCount: r.overdueCount,
      moraOutstanding: Number(r.moraOutstanding),
      adminFeeOutstanding: Number(r.adminFeeOutstanding),
      totalOutstanding: Number(r.totalOutstanding),
      nextDueDate: r.nextDueDate ?? '',
      waterRightStatus: WATER_LABEL[r.waterRightStatus] ?? r.waterRightStatus,
    });
  });
  finishSheet(cartera);

  const clientes = wb.addWorksheet('Clientes');
  clientes.columns = [
    { header: 'Código', key: 'code', width: 12 },
    { header: 'Nombre', key: 'fullName', width: 28 },
    { header: 'Tipo doc.', key: 'documentType', width: 12 },
    { header: 'Documento', key: 'documentNumber', width: 20 },
    { header: 'Email', key: 'email', width: 26 },
    { header: 'Teléfono', key: 'phone', width: 16 },
    { header: 'Dirección', key: 'address', width: 36 },
  ];
  clients.forEach((r) => {
    clientes.addRow({
      code: r.code ?? '',
      fullName: r.fullName,
      documentType: r.documentType,
      documentNumber: r.documentNumber,
      email: r.email ?? '',
      phone: r.phone ?? '',
      address: r.address ?? '',
    });
  });
  finishSheet(clientes);

  await saveWorkbook(wb, `Cartera_${slug(projectLabel)}_${today()}.xlsx`);
}

export async function exportEstados(
  projectId: string,
  projectLabel: string,
  status: ReportStatus,
  format: 'pdf' | 'excel',
): Promise<void> {
  const statements = await getEstadosCuenta(projectId, status);
  if (statements.length === 0) throw new EmptyExportError();

  const base = `EstadosCuenta_${slug(projectLabel)}_${today()}`;

  if (format === 'pdf') {
    exportBulkStatementsPDF(
      statements.map((s) => ({ contract: s, payments: s.payments })),
      `${base}.pdf`,
    );
    return;
  }

  const wb = await newWorkbook();

  const resumen = wb.addWorksheet('Resumen');
  resumen.columns = [
    { header: 'Contrato', key: 'contractNumber', width: 16 },
    { header: 'Cliente', key: 'buyerName', width: 28 },
    { header: 'Tipo', key: 'saleType', width: 12 },
    { header: 'Estado', key: 'status', width: 12 },
    { header: 'Financiado', key: 'financedAmount', width: 16, style: { numFmt: MONEY_FMT } },
    { header: 'Pagado', key: 'totalPaid', width: 16, style: { numFmt: MONEY_FMT } },
    { header: 'Saldo lote', key: 'outstanding', width: 16, style: { numFmt: MONEY_FMT } },
    { header: 'Mora', key: 'moraOutstanding', width: 14, style: { numFmt: MONEY_FMT } },
    { header: 'Costo adm. pend.', key: 'adminFeeOutstanding', width: 16, style: { numFmt: MONEY_FMT } },
    { header: 'Saldo total', key: 'totalOutstanding', width: 16, style: { numFmt: MONEY_FMT } },
    { header: 'Cuotas vencidas', key: 'overdueCount', width: 15 },
  ];
  statements.forEach((s) => {
    resumen.addRow({
      contractNumber: s.contractNumber,
      buyerName: s.buyerName,
      saleType: SALE_LABEL[s.saleType] ?? s.saleType,
      status: STATUS_LABEL[s.status] ?? s.status,
      financedAmount: Number(s.financedAmount),
      totalPaid: Number(s.balance?.totalPaid ?? '0'),
      outstanding: Number(s.balance?.outstanding ?? '0'),
      moraOutstanding: Number(s.balance?.moraOutstanding ?? '0'),
      adminFeeOutstanding: Number(s.balance?.adminFeeOutstanding ?? '0'),
      totalOutstanding: Number(s.balance?.totalOutstanding ?? '0'),
      overdueCount: s.balance?.overdueCount ?? 0,
    });
  });
  finishSheet(resumen);

  const cuotas = wb.addWorksheet('Cuotas');
  cuotas.columns = [
    { header: 'Contrato', key: 'contractNumber', width: 16 },
    { header: '#', key: 'installmentNumber', width: 6 },
    { header: 'Vencimiento', key: 'dueDate', width: 14 },
    { header: 'Cuota', key: 'totalAmount', width: 14, style: { numFmt: MONEY_FMT } },
    { header: 'Capital', key: 'principalAmount', width: 14, style: { numFmt: MONEY_FMT } },
    { header: 'Interés', key: 'interestAmount', width: 14, style: { numFmt: MONEY_FMT } },
    { header: 'Costo adm.', key: 'adminFeeAmount', width: 14, style: { numFmt: MONEY_FMT } },
    { header: 'Pagado capital', key: 'paidPrincipal', width: 15, style: { numFmt: MONEY_FMT } },
    { header: 'Pagado interés', key: 'paidInterest', width: 15, style: { numFmt: MONEY_FMT } },
    { header: 'Saldo', key: 'closingBalance', width: 14, style: { numFmt: MONEY_FMT } },
    { header: 'Estado', key: 'status', width: 12 },
  ];
  statements.forEach((s) => {
    s.installments.forEach((r) => {
      cuotas.addRow({
        contractNumber: s.contractNumber,
        installmentNumber: r.installmentNumber,
        dueDate: r.dueDate,
        totalAmount: Number(r.totalAmount),
        principalAmount: Number(r.principalAmount),
        interestAmount: Number(r.interestAmount),
        adminFeeAmount: Number(r.adminFeeAmount ?? '0'),
        paidPrincipal: Number(r.paidPrincipal),
        paidInterest: Number(r.paidInterest),
        closingBalance: Number(r.closingBalance),
        status: INST_STATUS[r.displayStatus ?? r.status] ?? (r.displayStatus ?? r.status),
      });
    });
  });
  finishSheet(cuotas);

  const pagos = wb.addWorksheet('Pagos');
  pagos.columns = [
    { header: 'Contrato', key: 'contractNumber', width: 16 },
    { header: 'Fecha', key: 'paymentDate', width: 14 },
    { header: 'Monto', key: 'amount', width: 14, style: { numFmt: MONEY_FMT } },
    { header: 'Método', key: 'method', width: 14 },
    { header: 'Referencia', key: 'reference', width: 20 },
  ];
  statements.forEach((s) => {
    s.payments.forEach((p) => {
      pagos.addRow({
        contractNumber: s.contractNumber,
        paymentDate: p.paymentDate,
        amount: Number(p.amount),
        method: PAY_METHOD[p.method] ?? p.method,
        reference: p.reference ?? '',
      });
    });
  });
  finishSheet(pagos);

  await saveWorkbook(wb, `${base}.xlsx`);
}
